import styled from "styled-components";
import { FC } from "react";

import { BaseComponentProps } from "../types";
import { primaryActive, primaryInactive } from "../../typography/alpha";
import { FontWeight, ItemSize } from "../../types/components";
import { getFontSize } from "../../utils/components";

export enum RadioButtonTestIds {
  Container = "radio_button",
}

const Input = styled.input`
  position: absolute;
  height: 0;
  width: 0;
  opacity: 0;
  z-index: -1;
`;

type MarkerProps = {
  $size?: ItemSize;
};

const Marker = styled.div<MarkerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }): number =>
    $size === "medium" || $size === "large" ? 24 : 16}px;
  height: ${({ $size }): number =>
    $size === "medium" || $size === "large" ? 24 : 16}px;
  border: 1px solid var(--color-primary-400);
  border-radius: 100%;
  box-sizing: border-box;

  &::after {
    content: "";
    width: ${({ $size }): number =>
      $size === "medium" || $size === "large" ? 14 : 8}px;
    height: ${({ $size }): number =>
      $size === "medium" || $size === "large" ? 14 : 8}px;
    border-radius: 100%;
    background-color: var(--color-secondary-800);
    display: none;
  }

  ${Input}:checked + & {
    border-color: var(--color-secondary-800);
  }

  ${Input}:checked + &::after {
    display: block;
  }

  ${Input}:disabled + & {
    border-color: var(--color-primary-200);
  }
`;

type LabelProps = {
  $disabled?: boolean;
  $size: ItemSize;
  $fontWeight: FontWeight;
};

const Label = styled.label<LabelProps>`
  display: grid;
  grid-template-columns: min-content max-content;
  grid-template-areas: "marker label";
  align-items: center;
  width: max-content;
  ${({ $disabled }) =>
    $disabled ? primaryInactive.subtitle3 : primaryActive.subtitle3};
  cursor: ${({ $disabled }): string => ($disabled ? "not-allowed" : "pointer")};
  font-size: ${({ $size }): number => getFontSize($size)}px;
  font-weight: var(
    --font-weight-${({ $fontWeight }): FontWeight => $fontWeight}
  );

  span {
    grid-area: label;
    user-select: none;
    margin-left: 4px;
  }

  ${Marker} {
    grid-area: marker;
  }
`;

type Props = Omit<BaseComponentProps<"input">, "size"> & {
  id: string;
  testId?: string;
  className?: string;
  label?: string;
  size?: ItemSize;
  fontWeight?: FontWeight;
};

export const RadioButton: FC<Props> = ({
  id,
  testId,
  className,
  label,
  size = "regular" as ItemSize,
  fontWeight = "regular" as FontWeight,
  disabled,
  ...args
}) => (
  <Label
    htmlFor={id}
    data-testid={testId ?? RadioButtonTestIds.Container}
    $disabled={disabled}
    $size={size}
    $fontWeight={fontWeight}
    className={className}
  >
    {Boolean(label) && <span>{label}</span>}
    <Input
      aria-label={label}
      id={id}
      type="radio"
      {...args}
      disabled={disabled}
    />
    <Marker $size={size} />
  </Label>
);
